import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Users, TrendingUp, Star, Zap } from 'lucide-react';
import { PRODUCT_DEMO } from '../constants';
import { useLanguage } from '../LanguageContext';

/* ─── Logo PitBoard SVG inline (mockup sidebar) ─────────────────────────────── */
const PitboardLogoMark = () => (
  <svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <rect width="28" height="28" rx="6" fill="#0A192F"/>
    <rect x="7" y="7" width="3" height="14" fill="white" rx="1"/>
    <path d="M10 7h6a4 4 0 0 1 0 8h-6z" fill="white"/>
    <rect x="7" y="19" width="10" height="2" rx="1" fill="#0055A4"/>
  </svg>
);

/* ─── Carte glassmorphism flottante ─────────────────────────────────────────── */
function FloatCard({ icon, value, label, color, animClass }) {
  return (
    <div className={`pb-demo-float-card ${animClass}`}>
      <div className="pb-demo-float-icon" style={{ background: `${color}22`, color }}>
        {icon}
      </div>
      <div>
        <div className="pb-demo-float-val">{value}</div>
        <div className="pb-demo-float-lbl">{label}</div>
      </div>
    </div>
  );
}

/* ─── Vue Dashboard ─────────────────────────────────────────────────────────── */
function ViewDashboard({ t }) {
  const BARS = [
    { h: 42, c: '#CBD5E1' }, { h: 58, c: '#CBD5E1' },
    { h: 38, c: '#CBD5E1' }, { h: 75, c: '#0055A4' },
    { h: 52, c: '#CBD5E1' }, { h: 88, c: '#0055A4' },
    { h: 65, c: '#CBD5E1' }, { h: 80, c: '#0055A4' },
    { h: 72, c: '#CBD5E1' }, { h: 95, c: '#0A192F' },
  ];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        {t.dashStats.map((s, i) => (
          <div key={i} style={{ background: '#F7F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 17, fontWeight: 800, color: '#0A192F', lineHeight: 1 }}>{s.v}</div>
            <div style={{ fontSize: 9.5, color: '#94A3B8', marginTop: 3 }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#F7F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: '10px 12px', height: 90, display: 'flex', alignItems: 'flex-end', gap: 5, marginBottom: 14 }}>
        {BARS.map((b, i) => <div key={i} style={{ flex: 1, height: `${b.h}%`, background: b.c, borderRadius: '3px 3px 0 0' }} />)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {t.dashActivity.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F7F9FC', borderRadius: 7, padding: '6px 10px', fontSize: 10.5, color: '#475569' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: a.c, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{a.t}</span>
            <span style={{ fontSize: 9, color: '#94A3B8' }}>{a.time}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Vue CRM ───────────────────────────────────────────────────────────────── */
function ViewCRM({ t }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>{t.crmFilters}</span>
        <span style={{ fontSize: 10, fontWeight: 700, background: '#0055A4', color: 'white', padding: '3px 10px', borderRadius: 9999 }}>{t.crmNewClient}</span>
      </div>
      <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr .8fr .8fr', background: '#F7F9FC', padding: '7px 12px', gap: 8 }}>
          {t.crmHeader.map(h => (
            <span key={h} style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
          ))}
        </div>
        {t.crmClients.map((c, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr .8fr .8fr', padding: '8px 12px', gap: 8, borderTop: '1px solid rgba(0,0,0,0.04)', alignItems: 'center', background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#0A192F' }}>{c.nom}</span>
            <span style={{ fontSize: 10, color: '#475569' }}>{c.type}</span>
            <span style={{ fontSize: 10, color: '#475569', textAlign: 'center' }}>{c.contrats}</span>
            <span style={{ fontSize: 9.5, fontWeight: 600, color: c.color }}>{c.statut}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Vue Devis ─────────────────────────────────────────────────────────────── */
function ViewDevis({ t }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {t.devisStats.map((s, i) => (
          <div key={i} style={{ flex: 1, background: '#F7F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: '9px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 18, fontWeight: 800, color: '#0A192F' }}>{s.v}</div>
            <div style={{ fontSize: 9.5, color: '#94A3B8' }}>{s.l}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {t.devis.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.05)', background: '#fff' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#0055A4', minWidth: 72 }}>{d.ref}</span>
            <span style={{ fontSize: 10.5, color: '#0A192F', flex: 1 }}>{d.client}</span>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#0A192F', minWidth: 55, textAlign: 'right' }}>{d.montant}</span>
            <span style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, background: d.bg, color: d.c, minWidth: 56, textAlign: 'center' }}>{d.statut}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Vue Messagerie ────────────────────────────────────────────────────────── */
function ViewMessagerie({ t }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0A192F' }}>{t.msgTitle}</span>
        <span style={{ fontSize: 9.5, fontWeight: 700, background: '#EF4135', color: 'white', padding: '2px 8px', borderRadius: 9999 }}>{t.msgUnread}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {t.mails.map((m, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, background: m.unread ? '#EFF6FF' : '#FAFBFC', border: `1px solid ${m.unread ? 'rgba(0,85,164,0.1)' : 'rgba(0,0,0,0.05)'}`, cursor: 'default' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.unread ? '#0055A4' : '#CBD5E1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}>
              {m.from[0]}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: m.unread ? 700 : 500, color: '#0A192F', marginBottom: 2 }}>{m.from}</div>
              <div style={{ fontSize: 9.5, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.obj}</div>
            </div>
            <span style={{ fontSize: 9.5, color: '#94A3B8', flexShrink: 0 }}>{m.time}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── Vue IA ────────────────────────────────────────────────────────────────── */
function ViewIA({ t }) {
  return (
    <>
      <div style={{ background: 'linear-gradient(135deg, #EFF6FF, #F0F7FF)', border: '1px solid rgba(0,85,164,0.1)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0055A4', marginBottom: 6 }}>{t.iaLabel}</div>
        <div style={{ fontSize: 10.5, color: '#0A192F', marginBottom: 8, lineHeight: 1.5 }}>{t.iaTitle}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {t.iaStats.map((s, i) => (
            <div key={i} style={{ flex: 1, background: 'white', borderRadius: 7, padding: '7px 10px', border: '1px solid rgba(0,85,164,0.08)' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0A192F', fontFamily: 'Outfit,sans-serif' }}>{s.v}</div>
              <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ background: '#0A192F', padding: '10px 14px', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{t.iaAssistant}</div>
        <div style={{ padding: '10px 14px', background: '#FAFBFC' }}>
          <div
            style={{ fontSize: 10.5, color: '#0A192F', lineHeight: 1.6, marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: `"${t.iaText}"` }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {t.iaActions.map(a => (
              <span key={a} style={{ fontSize: 9.5, fontWeight: 600, padding: '3px 10px', borderRadius: 9999, border: '1px solid rgba(0,85,164,0.15)', color: '#0055A4', cursor: 'default' }}>{a}</span>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Vue Commissions & Bordereaux IA ───────────────────────────────────────── */
function ViewCommissions({ t }) {
  return (
    <>
      {/* Statistiques */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {t.commStats.map((s, i) => (
          <div key={i} style={{ flex: 1, background: '#F7F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: '9px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 15, fontWeight: 800, color: '#0A192F' }}>{s.v}</div>
            <div style={{ fontSize: 9.5, color: '#94A3B8', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
      {/* Tableau bordereaux */}
      <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 1fr', background: '#F7F9FC', padding: '7px 12px', gap: 8 }}>
          {t.commHeader.map(h => (
            <span key={h} style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</span>
          ))}
        </div>
        {t.bordereaux.map((b, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 1fr', padding: '8px 12px', gap: 8, borderTop: '1px solid rgba(0,0,0,0.04)', alignItems: 'center', background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#0055A4' }}>{b.ref}</span>
            <span style={{ fontSize: 10.5, color: '#0A192F' }}>{b.courtier}</span>
            <span style={{ fontSize: 10.5, fontWeight: 600, color: '#0A192F', textAlign: 'right' }}>{b.montant}</span>
            <span style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 9999, background: b.bg, color: b.c, textAlign: 'center' }}>{b.statut}</span>
          </div>
        ))}
      </div>
      {/* Alerte IA */}
      <div
        style={{ background: 'linear-gradient(135deg, #FEF2F2, #FFF5F5)', border: '1px solid rgba(239,65,53,0.15)', borderRadius: 8, padding: '10px 14px', fontSize: 10.5, color: '#0A192F', lineHeight: 1.6 }}
        dangerouslySetInnerHTML={{ __html: `⚠️ ${t.commAI}` }}
      />
    </>
  );
}

/* ─── Vue PitBoard Builder ──────────────────────────────────────────────────── */
function ViewBuilder({ t }) {
  return (
    <div style={{ display: 'flex', gap: 10, height: '100%' }}>
      {/* Panneau blocs */}
      <div style={{ width: 88, flexShrink: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{t.builderBlocks}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {t.blocks.map((b, i) => (
            <div key={i} style={{ background: '#F7F9FC', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6, padding: '5px 8px', fontSize: 9.5, color: '#475569', cursor: 'default' }}>{b}</div>
          ))}
        </div>
      </div>
      {/* Canvas d'édition */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#0A192F' }}>{t.builderTitle}</span>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <span style={{ fontSize: 9.5, padding: '3px 10px', borderRadius: 9999, border: '1px solid rgba(0,0,0,0.1)', color: '#475569', cursor: 'default' }}>{t.builderPreview}</span>
            <span style={{ fontSize: 9.5, padding: '3px 10px', borderRadius: 9999, background: '#0055A4', color: 'white', cursor: 'default' }}>{t.builderPublish}</span>
          </div>
        </div>
        {/* Zone Hero */}
        <div style={{ border: '2px dashed rgba(0,85,164,0.25)', borderRadius: 8, padding: '10px 12px', marginBottom: 6, background: 'linear-gradient(135deg, #EFF6FF, #F0F7FF)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#0055A4', marginBottom: 4 }}>Hero</div>
          <div style={{ height: 6, background: 'rgba(0,85,164,0.18)', borderRadius: 3, marginBottom: 4, width: '68%' }} />
          <div style={{ height: 4, background: 'rgba(0,85,164,0.08)', borderRadius: 3, width: '45%' }} />
        </div>
        {/* Zone Features */}
        <div style={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 8, padding: '8px 12px', marginBottom: 6, background: '#FAFBFC' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 5 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{ height: 26, background: 'rgba(0,0,0,0.04)', borderRadius: 5 }} />
            ))}
          </div>
        </div>
        {/* Statut */}
        <div style={{ fontSize: 9, color: '#94A3B8', textAlign: 'right' }}>{t.builderStatus}</div>
      </div>
    </div>
  );
}

const SLIDE_VIEWS = [ViewDashboard, ViewCRM, ViewDevis, ViewMessagerie, ViewIA, ViewCommissions, ViewBuilder];

/* ─── Composant principal ───────────────────────────────────────────────────── */
export default function ProductDemo() {
  const { lang }  = useLanguage();
  const t         = PRODUCT_DEMO[lang];
  const slides    = t.slides;

  const [current, setCurrent] = useState(0);
  const [fading,  setFading]  = useState(false);

  const goTo = useCallback((idx) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(idx);
      setFading(false);
    }, 280);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [current, goTo, slides.length]);

  const prev      = () => goTo((current - 1 + slides.length) % slides.length);
  const next      = () => goTo((current + 1) % slides.length);
  const activeNav = slides[current].activeNav;
  const SlideView = SLIDE_VIEWS[current];

  return (
    <section className="pb-demo-section" id="demo" data-testid="pb-demo">
      <div className="pb-demo-orb-blue" />

      <div className="pb-container">
        {/* Header */}
        <div className="pb-demo-header pb-section">
          <div className="pb-label">{t.label}</div>
          <h2 className="pb-section-title">{t.title}</h2>
          <p className="pb-section-sub">{t.subtitle}</p>
        </div>

        {/* Scène 3 colonnes */}
        <div className="pb-demo-scene">

          {/* Cartes flottantes — gauche */}
          <div className="pb-demo-float-col">
            <FloatCard
              icon={<Users size={16} />}
              value={t.floatLeft[0].value}
              label={t.floatLeft[0].label}
              color={t.floatLeft[0].color}
              animClass="pb-flt-a"
            />
            <FloatCard
              icon={<TrendingUp size={16} />}
              value={t.floatLeft[1].value}
              label={t.floatLeft[1].label}
              color={t.floatLeft[1].color}
              animClass="pb-flt-b"
            />
          </div>

          {/* Device central */}
          <div className="pb-demo-3d-wrap">
            <div className="pb-demo-3d-screen" data-testid="pb-demo-3d-screen">
              {/* Barre navigateur */}
              <div className="pb-demo-browser-bar">
                <div className="pb-demo-browser-dots">
                  <span className="pb-demo-browser-dot" style={{ background: '#FF5F57' }} />
                  <span className="pb-demo-browser-dot" style={{ background: '#FFBD2E' }} />
                  <span className="pb-demo-browser-dot" style={{ background: '#28C840' }} />
                </div>
                <div className="pb-demo-browser-url">
                  app.pitboardsuite.com / {slides[current].label.toLowerCase().replace(' ', '-')}
                </div>
              </div>

              {/* Corps — sidebar + contenu */}
              <div className="pb-demo-browser-body">
                {/* Sidebar */}
                <nav className="pb-demo-sidebar">
                  <div className="pb-demo-sidebar-logo">
                    <div className="pb-demo-sidebar-logo-mark">
                      <PitboardLogoMark />
                    </div>
                    <span className="pb-demo-sidebar-logo-text">PitBoard</span>
                  </div>
                  {t.navItems.map((item, i) => (
                    <div key={item.label} className={`pb-demo-nav-item${activeNav === i ? ' active' : ''}`}>
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className={`pb-demo-badge${item.type === 'red' ? ' red' : ''}`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                  ))}
                </nav>

                {/* Contenu avec transition */}
                <main
                  className="pb-demo-main"
                  style={{
                    opacity: fading ? 0 : 1,
                    transform: fading ? 'scale(0.97)' : 'scale(1)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                  }}
                >
                  <div className="pb-demo-main-head">
                    <span className="pb-demo-main-title">{slides[current].label}</span>
                    <span className="pb-demo-main-date">{t.date}</span>
                  </div>
                  <SlideView t={t} />
                </main>
              </div>
            </div>
          </div>

          {/* Cartes flottantes — droite */}
          <div className="pb-demo-float-col pb-demo-float-right">
            <FloatCard
              icon={<Star size={16} />}
              value={t.floatRight[0].value}
              label={t.floatRight[0].label}
              color={t.floatRight[0].color}
              animClass="pb-flt-c"
            />
            <FloatCard
              icon={<Zap size={16} />}
              value={t.floatRight[1].value}
              label={t.floatRight[1].label}
              color={t.floatRight[1].color}
              animClass="pb-flt-d"
            />
          </div>
        </div>

        {/* Navigation carrousel */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 44, position: 'relative', zIndex: 2 }}>
          <button
            onClick={prev}
            data-testid="pb-demo-prev"
            style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            aria-label={t.prevLabel}
          >
            <ChevronLeft size={16} />
          </button>

          <div style={{ display: 'flex', gap: 6 }}>
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                data-testid={`pb-demo-tab-${s.id}`}
                style={{
                  padding: '6px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, transition: 'all 0.25s ease',
                  background: current === i ? 'rgba(255,255,255,0.15)' : 'transparent',
                  color: current === i ? '#fff' : 'rgba(255,255,255,0.45)',
                  outline: 'none',
                }}
                aria-pressed={current === i}
              >
                {s.label}
              </button>
            ))}
          </div>

          <button
            onClick={next}
            data-testid="pb-demo-next"
            style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            aria-label={t.nextLabel}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Barre de progression */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 14, position: 'relative', zIndex: 2 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                height: 3, borderRadius: 9999, transition: 'all 0.3s ease',
                background: current === i ? '#fff' : 'rgba(255,255,255,0.2)',
                width: current === i ? 28 : 8,
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
